import { SseEvent } from "@redchain/contracts";
import type { ApiClient } from "../api/client";
import { Backoff } from "./backoff";

export interface SseReaderOpts {
  url: string;
  api: ApiClient;
  onEvent: (ev: SseEvent, id: string | null) => void;
  onStatus?: (connected: boolean) => void;
  /** Fatal HTTP status (e.g. 409 superseded, 404 ended): stop reconnecting. */
  onFatal?: (status: number, reason: string) => void;
  lastEventId?: string | null;
}

/**
 * Fetch-based Server-Sent Events reader. Unlike EventSource it can send the bearer token
 * in `Authorization` and resume with `Last-Event-ID` after reconnects (API-9).
 */
export class SseReader {
  private abort: AbortController | null = null;
  private stopped = false;
  private lastEventId: string | null;
  private readonly backoff = new Backoff();

  constructor(private readonly opts: SseReaderOpts) {
    this.lastEventId = opts.lastEventId ?? null;
  }

  get lastId(): string | null {
    return this.lastEventId;
  }

  start(): void {
    this.stopped = false;
    void this.loop();
  }

  stop(): void {
    this.stopped = true;
    this.abort?.abort();
  }

  private async loop(): Promise<void> {
    while (!this.stopped) {
      this.abort = new AbortController();
      try {
        const status = await this.connect(this.abort.signal);
        if (status === "fatal") return;
      } catch (e) {
        if (this.stopped) return;
        console.debug("SSE disconnected", e);
      }
      this.opts.onStatus?.(false);
      if (this.stopped) return;
      await this.backoff.wait(this.abort.signal);
    }
  }

  private async connect(signal: AbortSignal): Promise<"closed" | "fatal"> {
    const headers: Record<string, string> = { accept: "text/event-stream" };
    if (this.opts.api.token) headers.authorization = `Bearer ${this.opts.api.token}`;
    if (this.lastEventId) headers["last-event-id"] = this.lastEventId;
    const res = await fetch(this.opts.url, { headers, signal, credentials: "include", cache: "no-store" });
    if (res.status === 401) {
      await this.opts.api.refresh();
      return "closed";
    }
    if (res.status === 404 || res.status === 409 || res.status === 410) {
      let reason = `http_${res.status}`;
      try {
        reason = ((await res.json()) as { reason?: string }).reason ?? reason;
      } catch {
        /* ignore */
      }
      this.opts.onFatal?.(res.status, reason);
      return "fatal";
    }
    if (!res.ok || !res.body) throw new Error(`SSE HTTP ${res.status}`);

    this.backoff.reset();
    this.opts.onStatus?.(true);
    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = "";
    let data: string[] = [];
    let eventId: string | null = null;
    let eventType = "";

    const dispatch = () => {
      if (data.length === 0) {
        eventType = "";
        return;
      }
      const raw = data.join("\n");
      data = [];
      if (eventId !== null) this.lastEventId = eventId;
      try {
        const json = JSON.parse(raw) as Record<string, unknown>;
        if (eventType && json.type === undefined) json.type = eventType;
        const parsed = SseEvent.safeParse(json);
        if (parsed.success) this.opts.onEvent(parsed.data, this.lastEventId);
        else console.warn("Unknown SSE event", json);
      } catch (e) {
        console.warn("Bad SSE payload", raw, e);
      }
      eventType = "";
    };

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      let nl: number;
      while ((nl = buffer.search(/\r\n|\r|\n/)) >= 0) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + (buffer.startsWith("\r\n", nl) ? 2 : 1));
        if (line === "") {
          dispatch();
          continue;
        }
        if (line.startsWith(":")) continue; // heartbeat comment
        const colon = line.indexOf(":");
        const field = colon >= 0 ? line.slice(0, colon) : line;
        let val = colon >= 0 ? line.slice(colon + 1) : "";
        if (val.startsWith(" ")) val = val.slice(1);
        if (field === "data") data.push(val);
        else if (field === "id") eventId = val;
        else if (field === "event") eventType = val;
      }
    }
    return "closed";
  }
}
